import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllFaqsGrouped } from "../api/faqApi";
import { ChevronDown, Search, X, ArrowLeft } from "lucide-react";
import * as LucideIcons from "lucide-react";
import FaqBlockRenderer from "../components/FaqBlockRenderer";
import { FaqPageShimmer } from "../components/ShimmerEffect";

const FaqPage = () => {
  const [groupedFaqs, setGroupedFaqs] = useState([]);
  const [activeTopic, setActiveTopic] = useState("");
  const [openFaqId, setOpenFaqId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchFaqs = async () => {
      try {
        const response = await getAllFaqsGrouped();
        if (response.success && response.data.length > 0) {
          setGroupedFaqs(response.data);
          setActiveTopic(response.data[0].name);
        }
      } catch (error) {
        console.error("Error fetching FAQs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  const handleTopicChange = (topicName) => {
    setActiveTopic(topicName);
    setOpenFaqId(null);
    setSearchQuery("");
  };

  // Extract plain text from EditorJS answer for search
  const getTextFromAnswer = (answer) => {
    if (typeof answer === "string") return answer;
    if (answer?.blocks) {
      return answer.blocks
        .map((block) => {
          if (block.data.text) return block.data.text.replace(/<[^>]+>/g, "");
          if (block.data.items)
            return block.data.items
              .map((i) =>
                typeof i === "string"
                  ? i.replace(/<[^>]+>/g, "")
                  : (i.content || "").replace(/<[^>]+>/g, "")
              )
              .join(" ");
          return "";
        })
        .join(" ");
    }
    return "";
  };

  const isSearching = searchQuery.trim().length > 0;
  const currentTopicData = groupedFaqs.find((t) => t.name === activeTopic);

  let displayedFaqs = [];
  if (isSearching) {
    const q = searchQuery.toLowerCase();
    groupedFaqs.forEach((topic) => {
      topic.faqs.forEach((faq) => {
        if (
          faq.question.toLowerCase().includes(q) ||
          getTextFromAnswer(faq.answer).toLowerCase().includes(q)
        ) {
          displayedFaqs.push({ ...faq, topicName: topic.name, topicIcon: topic.icon });
        }
      });
    });
  } else {
    displayedFaqs = currentTopicData?.faqs || [];
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[var(--surface-primary)] transition-colors duration-300">

      {/* ── Hero / Header strip ── */}
      <div className="bg-white dark:bg-[var(--surface-card)] border-b border-gray-100 dark:border-[var(--surface-border)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-1">
          <Link
            to="/#faq"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold bg-gray-50 dark:bg-[var(--surface-secondary)] text-gray-700 dark:text-[var(--text-primary)] border border-gray-200 dark:border-[var(--surface-border)] hover:border-orange-500 hover:text-orange-500 dark:hover:border-orange-400 dark:hover:text-orange-400 transition-all duration-200 shadow-sm group"
          >
            <ArrowLeft size={16} className="transition-transform duration-200 group-hover:-translate-x-0.5" />
            <span>Back to Home FAQ</span>
          </Link>
        </div>
        <div className="max-w-3xl mx-auto px-4 pb-6 sm:pb-8 pt-2 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-[var(--text-primary)] mb-4">
            We Care About Your Questions
          </h1>

          {/* Search bar */}
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400 group-focus-within:text-orange-500 transition-colors" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setOpenFaqId(null);
              }}
              className="w-full pl-11 pr-10 py-3.5 bg-gray-50 dark:bg-[var(--surface-secondary)] border border-gray-200 dark:border-[var(--surface-border)] rounded-xl text-sm text-gray-900 dark:text-[var(--text-primary)] placeholder-gray-400 dark:placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-all"
              placeholder="Search any question or keyword…"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-4 flex items-center"
              >
                <X className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Main Body ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="py-2">
            <FaqPageShimmer />
          </div>
        ) : groupedFaqs.length === 0 ? (
          <div className="text-center py-24 text-gray-400 dark:text-[var(--text-muted)] text-sm">
            No FAQs available at the moment.
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6">

            {/* ── LEFT: Topics ── */}
            {/* Mobile: pill strip */}
            <div className="lg:hidden flex overflow-x-auto gap-2 pb-1 scrollbar-none">
              {groupedFaqs.map((topic) => {
                const Icon = LucideIcons[topic.icon] || LucideIcons.HelpCircle;
                const isActive = !isSearching && activeTopic === topic.name;
                return (
                  <button
                    key={topic.name}
                    onClick={() => handleTopicChange(topic.name)}
                    className={`flex items-center gap-2 px-3.5 py-2 rounded-xl whitespace-nowrap text-xs font-semibold shrink-0 border transition-all duration-200 ${
                      isActive
                        ? "bg-orange-500 text-white border-orange-500 shadow-sm"
                        : "bg-white dark:bg-[var(--surface-card)] text-gray-600 dark:text-[var(--text-secondary)] border-gray-200 dark:border-[var(--surface-border)] hover:border-orange-300"
                    }`}
                  >
                    <Icon size={13} />
                    {topic.name}
                  </button>
                );
              })}
            </div>

            {/* Desktop: sidebar */}
            <div className="hidden lg:block w-60 xl:w-64 shrink-0">
              <div className="bg-white dark:bg-[var(--surface-card)] rounded-2xl border border-gray-100 dark:border-[var(--surface-border)] shadow-sm overflow-hidden sticky top-6">
                <p className="text-[11px] font-semibold text-gray-400 dark:text-[var(--text-muted)] uppercase tracking-widest px-4 pt-4 pb-2">
                  Browse by Topic
                </p>
                <div className="flex flex-col px-2 pb-2 gap-0.5">
                  {groupedFaqs.map((topic) => {
                    const Icon = LucideIcons[topic.icon] || LucideIcons.HelpCircle;
                    const isActive = !isSearching && activeTopic === topic.name;
                    return (
                      <button
                        key={topic.name}
                        onClick={() => handleTopicChange(topic.name)}
                        className={`flex items-center gap-2.5 w-full px-3 py-2.5 rounded-xl text-left text-sm transition-all duration-150 ${
                          isActive
                            ? "bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 font-semibold"
                            : "text-gray-600 dark:text-[var(--text-secondary)] hover:bg-gray-50 dark:hover:bg-[var(--surface-hover)] font-medium"
                        }`}
                      >
                        <Icon
                          size={16}
                          strokeWidth={1.8}
                          className={isActive ? "text-orange-500" : "text-gray-400 dark:text-[var(--text-faint)]"}
                        />
                        <span className="leading-tight flex-1">{topic.name}</span>
                        <span className={`text-[11px] px-1.5 py-0.5 rounded-md font-semibold ${
                          isActive
                            ? "bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-300"
                            : "bg-gray-100 dark:bg-[var(--surface-elevated)] text-gray-400 dark:text-[var(--text-faint)]"
                        }`}>
                          {topic.faqs.length}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── RIGHT: Questions ── */}
            <div className="flex-1 min-w-0">
              {/* Section label */}
              <div className="flex items-center justify-between mb-4">
                {isSearching ? (
                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-[var(--text-primary)]">
                      Results for <span className="text-orange-500">"{searchQuery}"</span>
                    </p>
                    <p className="text-xs text-gray-400 dark:text-[var(--text-muted)] mt-0.5">
                      {displayedFaqs.length} match{displayedFaqs.length !== 1 ? "es" : ""} found
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {(() => {
                      const ActiveIcon = LucideIcons[currentTopicData?.icon] || LucideIcons.HelpCircle;
                      return (
                        <>
                          <div className="w-7 h-7 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                            <ActiveIcon size={15} className="text-orange-500" strokeWidth={1.8} />
                          </div>
                          <p className="text-sm font-semibold text-gray-800 dark:text-[var(--text-primary)]">
                            {activeTopic}
                          </p>
                          <span className="text-xs text-gray-400 dark:text-[var(--text-muted)]">
                            · {displayedFaqs.length} question{displayedFaqs.length !== 1 ? "s" : ""}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Accordion list */}
              {displayedFaqs.length === 0 ? (
                <div className="text-center py-16 text-gray-400 dark:text-[var(--text-faint)] text-sm">
                  {isSearching ? "No results found. Try a different keyword." : "No questions in this topic yet."}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {displayedFaqs.map((faq) => {
                    const isOpen = openFaqId === faq._id;
                    return (
                      <div
                        key={faq._id}
                        className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                          isOpen
                            ? "border-orange-200 dark:border-orange-800/50 bg-white dark:bg-[var(--surface-card)] shadow-sm"
                            : "border-gray-100 dark:border-[var(--surface-border)] bg-white dark:bg-[var(--surface-card)] hover:border-gray-200 dark:hover:border-[var(--surface-border)]"
                        }`}
                      >
                        {/* Question button */}
                        <button
                          onClick={() => setOpenFaqId(isOpen ? null : faq._id)}
                          className="flex items-start justify-between w-full px-5 py-4 text-left focus:outline-none group gap-3"
                        >
                          <div className="flex-1 min-w-0">
                            {/* Topic badge shown only during search */}
                            {isSearching && faq.topicName && (
                              <div className="flex items-center gap-1 text-[10px] font-bold text-orange-500 dark:text-orange-400 uppercase tracking-wider mb-1.5">
                                {React.createElement(
                                  LucideIcons[faq.topicIcon] || LucideIcons.HelpCircle,
                                  { size: 10 }
                                )}
                                {faq.topicName}
                              </div>
                            )}
                            <span
                              className={`text-sm font-medium leading-snug transition-colors ${
                                isOpen
                                  ? "text-orange-600 dark:text-orange-400"
                                  : "text-gray-800 dark:text-[var(--text-primary)] group-hover:text-gray-900"
                              }`}
                            >
                              {faq.question}
                            </span>
                          </div>

                          {/* Chevron */}
                          <div
                            className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 mt-0.5 ${
                              isOpen
                                ? "bg-orange-500 text-white rotate-180"
                                : "bg-gray-100 dark:bg-[var(--surface-elevated)] text-gray-400 dark:text-[var(--text-muted)] group-hover:bg-gray-200 dark:group-hover:bg-[var(--surface-hover)]"
                            }`}
                          >
                            <ChevronDown size={14} strokeWidth={2.5} />
                          </div>
                        </button>

                        {/* Answer */}
                        <div
                          className={`transition-all duration-300 ease-in-out overflow-hidden ${
                            isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
                          }`}
                        >
                          <div className="px-5 pb-5 pt-0 border-t border-gray-100 dark:border-[var(--surface-border)]">
                            <div className="pt-4">
                              {typeof faq.answer === "string" ? (
                                <p className="text-sm leading-relaxed text-gray-600 dark:text-[var(--text-secondary)] whitespace-pre-wrap">
                                  {faq.answer}
                                </p>
                              ) : (
                                <FaqBlockRenderer blocks={faq.answer?.blocks} />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FaqPage;
